import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Bolsa, DiscardBolsasResult } from '../../../types';
import { BolsasService } from '../../../services/bolsasService';

const INK = '#2E2430';
const MUTED = '#A79AA7';
const HAIR = '#F0E9F0';
const RED = '#CB4444';

interface BagDeleteConfirmProps {
  open: boolean;
  bolsas: Bolsa[];
  onCancel: () => void;
  onConfirmed: (result: DiscardBolsasResult) => void;
}

// SPEC-119 (RN-002, RN-007, UC-006): único componente autorizado a llamar
// BolsasService.discardBolsas — converge aquí la papelera del chip, el botón del encabezado
// de contenido y el borrado en lote de "Gestionar bolsas".
export function BagDeleteConfirm({ open, bolsas, onCancel, onConfirmed }: BagDeleteConfirmProps) {
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirmar = async () => {
    setBorrando(true);
    setError(null);
    try {
      const result = await BolsasService.discardBolsas(bolsas.map(b => b.id));
      onConfirmed(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al borrar');
    } finally {
      setBorrando(false);
    }
  };

  const esLote = bolsas.length > 1;

  return (
    <Dialog open={open} onClose={borrando ? undefined : onCancel} PaperProps={{ sx: { borderRadius: '20px', width: 470, maxWidth: '92vw' } }}>
      <Box sx={{ padding: '28px 30px', maxHeight: '86vh', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: RED }}>
          BORRAR BOLSA{esLote ? '(S)' : ''}
        </Typography>
        <Typography sx={{ fontSize: 23, fontWeight: 700, color: INK, mt: '4px' }}>
          {esLote ? `¿Borrar ${bolsas.length} bolsas?` : `¿Borrar ${bolsas[0]?.codigo_bolsa ?? bolsas[0]?.nombre}?`}
        </Typography>
        <Typography sx={{ fontSize: 16, color: MUTED, mt: '6px', mb: '14px' }}>
          Revisa el contenido antes de confirmar — esto es lo que se perderá:
        </Typography>

        {bolsas.map(bolsa => {
          const lineas = bolsa.lineas ?? [];
          const unidades = lineas.reduce((acc, l) => acc + l.cantidad, 0);
          return (
            <Box
              key={bolsa.id}
              data-testid={`delete-confirm-row-${bolsa.id}`}
              sx={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid ${HAIR}`, borderRadius: '12px', padding: '11px 13px', mb: '8px' }}
            >
              <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                {lineas.slice(0, 4).map(l => (
                  <Box
                    key={l.id}
                    component={l.imagen_url ? 'img' : 'div'}
                    src={l.imagen_url ?? undefined}
                    sx={{ width: 24, height: 24, borderRadius: '7px', border: `1px solid ${HAIR}`, objectFit: 'cover', background: '#fff', flexShrink: 0 }}
                  />
                ))}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: INK }}>
                  {bolsa.codigo_bolsa ?? bolsa.nombre} · {bolsa.cliente_razon_social ?? 'Sin cliente asociado'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: MUTED }}>
                  {bolsa.lineas_count} referencias · {unidades.toLocaleString('es-CO')} unds · $ {bolsa.total.toLocaleString('es-CO')}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Box sx={{ display: 'flex', gap: '8px', background: 'rgba(203,68,68,.07)', borderRadius: '10px', padding: '12px', mt: '10px' }}>
          <WarningAmberIcon sx={{ color: RED, fontSize: 22, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 15, color: INK }}>
            Esta acción no se puede deshacer. Las cotizaciones ya convertidas desde esta(s) bolsa(s)
            no se ven afectadas — conservan su snapshot.
          </Typography>
        </Box>

        {error && (
          <Typography data-testid="delete-confirm-error" sx={{ fontSize: 15, color: RED, mt: '10px' }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: '20px' }}>
          <Button
            data-testid="btn-cancelar-borrado"
            onClick={onCancel}
            disabled={borrando}
            variant="outlined"
            sx={{ borderColor: HAIR, color: INK, textTransform: 'none', borderRadius: '999px' }}
          >
            Cancelar
          </Button>
          <Button
            data-testid="btn-confirmar-borrado"
            onClick={handleConfirmar}
            disabled={borrando}
            variant="contained"
            sx={{ background: RED, textTransform: 'none', borderRadius: '999px', '&:hover': { background: RED, filter: 'brightness(.9)' } }}
          >
            {borrando ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Borrar definitivamente'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
